import { forwardRef, useEffect, useState } from "react";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { useCheckbox } from "@tamagui/checkbox-headless";
import { isWeb, Stack, styled, View as TamaguiView, useProps } from "@tamagui/core";
import { Checkmark } from "../../../__generated__/icons";
import { useObservabilitySafe } from "../../../helpers/useObservabilitySafe";
import { isKeyOfObject } from "../../../helpers/utils";
import { Box } from "../../Box";
import { TrackableContainer } from "../../shared/TrackableContainer";
import { Text } from "../../Text";
import { useInputId } from "../shared/hooks";
import { useCheckBoxRadioState } from "./shared/hooks";
import { jsx, jsxs } from "react/jsx-runtime";
const { radioCheckbox: checkboxTokens } = tokens, SimpleCheckbox = forwardRef(function({
  analyticsName,
  label,
  value,
  defaultChecked = !1,
  disabled,
  invalid,
  id,
  onPress,
  name,
  trackImpression,
  checked,
  ariaLabel,
  ...props
}, ref) {
  const { size = "medium" } = useProps(props), { trackJourneyEvent } = useObservabilitySafe(), [internalChecked, setInternalChecked] = useState(defaultChecked);
  useEffect(() => {
    checked !== void 0 && setInternalChecked(checked);
  }, [checked]);
  const onCheckedChange = (e) => {
    e.stopPropagation(), onPress && onPress(value, !internalChecked), trackJourneyEvent("platform", "checkboxClickEvent", { targetName: analyticsName, optionName: value });
  }, { checkboxProps, checkboxRef, bubbleInput } = useCheckbox(
    {
      name,
      value,
      defaultChecked,
      disabled,
      onPress: onCheckedChange,
      ...props
    },
    [internalChecked, setInternalChecked],
    ref
  ), inputId = useInputId(id), { rowState, buttonState, handleFocus, handleBlur } = useCheckBoxRadioState({
    checked: internalChecked,
    disabled,
    invalid
  }), { checkMarkSize, checkMarkColor } = getCheckMarkIconStyle(size, buttonState);
  return /* @__PURE__ */ jsx(
    TrackableContainer,
    {
      analyticsName,
      trackImpression,
      eventName: "checkboxViewEvent",
      children: /* @__PURE__ */ jsxs(
        CheckboxRowButton,
        {
          ...checkboxProps,
          "aria-invalid": isWeb ? invalid : void 0,
          "aria-required": isWeb ? props.required : void 0,
          "aria-label": ariaLabel,
          ref: checkboxRef,
          size,
          state: rowState,
          onFocus: handleFocus,
          onBlur: handleBlur,
          focusable: !disabled,
          id: inputId,
          children: [
            /* @__PURE__ */ jsxs(
              Checkbox,
              {
                disabled,
                size,
                state: { state: buttonState, checked: internalChecked },
                onFocus: handleFocus,
                onBlur: handleBlur,
                focusable: !1,
                alignSelf: "flex-start",
                children: [
                  internalChecked === !0 && /* @__PURE__ */ jsx(Checkmark, { size: checkMarkSize, color: checkMarkColor }),
                  bubbleInput
                ]
              }
            ),
            label && /* @__PURE__ */ jsx(Box, { alignSelf: "center", flex: 1, children: /* @__PURE__ */ jsx(
              Text,
              {
                alignSelf: "flex-start",
                textAlign: "left",
                tag: "label",
                htmlFor: id,
                cursor: "inherit",
                typography: checkboxTokens.typography.simpleCheckbox[size],
                color: isKeyOfObject(buttonState, checkboxTokens.color.content.valueText.state) ? checkboxTokens.color.content.valueText.state[buttonState] : checkboxTokens.color.content.valueText.default,
                children: label
              }
            ) })
          ]
        }
      )
    }
  );
}), CheckboxRowButton = styled(TamaguiView, {
  tag: "button",
  display: "flex",
  flexDirection: "row",
  /**
   * Combined size and state into one variant because there are multiple times we need to use both to determine the style (especially for small/xsmall)
   * Seperating these two variants causes confusion and causes strange overrides
   */
  variants: {
    state: (state) => ({
      cursor: state === "inactive" ? "default" : "pointer"
    }),
    size: (size) => {
      const updatedSize = size === "xsmall" ? "small" : size;
      return {
        borderColor: "$transparent",
        backgroundColor: "$transparent",
        paddingTop: checkboxTokens.margin.simpleCheckbox.topBottom[updatedSize],
        paddingBottom: checkboxTokens.margin.simpleCheckbox.topBottom[updatedSize],
        gap: checkboxTokens.padding.simpleCheckbox[updatedSize]
      };
    }
  }
}), Checkbox = styled(Stack, {
  borderWidth: checkboxTokens.borderWidth.default,
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "auto",
  alignSelf: "center",
  variants: {
    size: (size) => {
      const widthAndHeight = {
        xsmall: checkboxTokens.size.element.radio.exterior.xsmall,
        small: checkboxTokens.size.element.radio.exterior.small,
        medium: checkboxTokens.size.element.radio.exterior.medium
      };
      return {
        borderRadius: checkboxTokens.cornerRadius.element.checkbox[size === "small" || size === "xsmall" ? "smallXsmall" : size],
        width: widthAndHeight[size],
        height: widthAndHeight[size]
      };
    },
    // eslint-disable-next-line jsdoc/require-jsdoc
    state: ({
      state,
      checked
    }) => ({
      borderColor: isKeyOfObject(state, checkboxTokens.color.border.element.checkbox.state) ? checkboxTokens.color.border.element.checkbox.state[state] : checkboxTokens.color.border.element.checkbox.default,
      backgroundColor: (checked || state === "inactive") && isKeyOfObject(state, checkboxTokens.color.background.element.checkboxRadio.state) ? checkboxTokens.color.background.element.checkboxRadio.state[state] : "$transparent"
    })
  }
}), getCheckMarkIconStyle = (size, state) => ({
  checkMarkSize: size === "small" ? 20 : 24,
  checkMarkColor: isKeyOfObject(state, checkboxTokens.color.content.element.checkIcon.state) ? checkboxTokens.color.content.element.checkIcon.state[state] : checkboxTokens.color.content.element.checkIcon.default
});
export {
  SimpleCheckbox
};
